import React, { useRef, useEffect } from 'react';
import { animate, useMotionValue } from 'framer-motion';
import styled from 'styled-components';

const StyledEtherealShadow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  opacity: ${props => props.opacity || 0.3};

  .wave-container {
    position: absolute;
    width: 100%;
    height: 100%;
    filter: ${props => props.animationEnabled ? `url(#${props.filterId})` : 'none'};
  }

  .wave-layer {
    position: absolute;
    width: 120%;
    height: 120%;
    left: -10%;
    top: -10%;
    background: ${props => props.color || 'rgba(0, 0, 0, 0.1)'};
    mask-image: ${props => props.maskImage || 'radial-gradient(circle at center, black 0%, transparent 70%)'};
    mask-size: ${props => props.sizing === 'stretch' ? '100% 100%' : 'cover'};
    mask-repeat: no-repeat;
    mask-position: center;
    will-change: transform;
  }

  .noise-overlay {
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
    background-size: ${props => (props.noiseScale || 1) * 200}px;
    background-repeat: repeat;
    opacity: ${props => (props.noiseOpacity || 0) / 2};
    pointer-events: none;
  }
`;

function mapRange(value, fromLow, fromHigh, toLow, toHigh) {
  if (fromLow === fromHigh) {
    return toLow;
  }
  const percentage = (value - fromLow) / (fromHigh - fromLow);
  return toLow + percentage * (toHigh - toLow);
}

const EtherealShadow = ({
  color = 'rgba(0, 0, 0, 0.15)',
  animation = { scale: 50, speed: 50 },
  noise = { opacity: 0.3, scale: 1 },
  sizing = 'fill',
  maskImage,
  opacity = 0.3,
  className,
  style,
}) => {
  const filterId = `ethereal-shadow-${Math.random().toString(36).substr(2, 9)}`;
  const waveLayerRef = useRef(null);
  const animationRef = useRef(null);
  const timeRef = useRef(0);

  const animationEnabled = animation && animation.scale > 0;
  const animationSpeed = animation ? mapRange(animation.speed, 1, 100, 0.5, 3) : 1;
  const waveIntensity = animation ? mapRange(animation.scale, 1, 100, 0.02, 0.08) : 0.02;

  useEffect(() => {
    if (!animationEnabled || !waveLayerRef.current) return;

    let animationFrameId;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      timeRef.current = elapsed * animationSpeed;

      if (waveLayerRef.current) {
        // Create continuous wavy distortion using sine waves
        const waveX = Math.sin(timeRef.current * 0.5) * waveIntensity;
        const waveY = Math.cos(timeRef.current * 0.7) * waveIntensity;
        const waveScale = 1 + Math.sin(timeRef.current * 0.3) * 0.02;
        
        waveLayerRef.current.style.transform = `
          translate(${waveX * 100}px, ${waveY * 100}px) 
          scale(${waveScale})
        `;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [animationEnabled, animationSpeed, waveIntensity]);

  // Default mask - radial gradient for soft shadow
  const defaultMask = maskImage || 'radial-gradient(circle at center, black 0%, transparent 70%)';

  return (
    <StyledEtherealShadow
      className={className}
      style={style}
      filterId={filterId}
      animationEnabled={animationEnabled}
      color={color}
      sizing={sizing}
      maskImage={defaultMask}
      noiseOpacity={noise?.opacity || 0}
      noiseScale={noise?.scale || 1}
      opacity={opacity}>
      <div className="wave-container">
        {animationEnabled && (
          <svg style={{ position: 'absolute', width: 0, height: 0 }}>
            <defs>
              <filter id={filterId}>
                {/* Create continuous wavy pattern */}
                <feTurbulence
                  result="waves"
                  numOctaves="3"
                  baseFrequency={`${waveIntensity * 0.5} ${waveIntensity * 0.3}`}
                  seed="0"
                  type="fractalNoise"
                >
                  <animate
                    attributeName="baseFrequency"
                    values={`${waveIntensity * 0.5} ${waveIntensity * 0.3};${waveIntensity * 0.6} ${waveIntensity * 0.4};${waveIntensity * 0.5} ${waveIntensity * 0.3}`}
                    dur={`${10 / animationSpeed}s`}
                    repeatCount="indefinite"
                  />
                </feTurbulence>
                {/* Smooth displacement for wavy effect */}
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="waves"
                  scale={animation.scale * 2}
                  xChannelSelector="R"
                  yChannelSelector="G"
                  result="displaced"
                />
                {/* Additional smoothing */}
                <feGaussianBlur
                  in="displaced"
                  stdDeviation="2"
                  result="blurred"
                />
                {/* Final output */}
                <feComposite
                  in="blurred"
                  in2="SourceGraphic"
                  operator="over"
                  result="output"
                />
              </filter>
            </defs>
          </svg>
        )}
        <div 
          ref={waveLayerRef}
          className="wave-layer"
          style={{
            transition: animationEnabled ? 'none' : 'transform 0.3s ease',
          }}
        />
      </div>

      {noise && noise.opacity > 0 && (
        <div className="noise-overlay" />
      )}
    </StyledEtherealShadow>
  );
};

export default EtherealShadow;
